// TODO: implement consistent error formatting on server
// TODO: implement consistent error handling on client

import { Auth } from 'aws-amplify';

import {
  PartialEditableAccount,
  Account,
  PartialEditableUser,
  AccountUser,
  Application,
  AppInfo,
  AccountApps,
  ApiKeyCounts,
  Bible,
  AppListRecord,
  AccountAppDetails,
  Plan,
  TermsAndConditions,
} from 'api-bible-shared-lib/utils/types';

interface GetAccountParams {
  accountId: string;
}

interface PutAccountParams {
  accountId: string;
  data: PartialEditableAccount;
}

interface AccountResponse {
  account: Account;
}

interface GetAccountUserParams {
  accountId: string;
  userId: string;
}

interface GetAccountAppApiKeyParams {
  accountId: string;
  appId: string;
  apiKey: string;
}

interface PutAccountUserParams {
  accountId: string;
  userId: string;
  data: PartialEditableUser;
}

interface AccountUserResponse {
  user: AccountUser;
}

interface GetAppReportParams {
  accountId: string;
  appId: string;
  startDate: string;
  endDate: string;
  summary?: 'year' | 'month';
}

interface AppReportResponse {
  analytics: {
    requestCount: { [date: string]: number };
    requestScriptureCount: Record<string, number>;
    requestVerseCount: Record<string, number>;
    viewCount: Record<string, number>;
    viewVerseCount: Record<string, number>;
    viewUserCount: Record<string, number>;
  };
  translations: Array<{
    id: string;
    name: string;
    abbreviation: string;
    type: string;
    requestCount: number;
    requestScriptureCount: number;
    requestVerseCount: number;
    viewCount: number;
    viewUserCount: number;
    viewVerseCount: number;
  }>;
}

interface GetAccountAppsParams {
  accountId: string;
}

interface PostAccountAppParams {
  accountId: string;
  data: AppInfo;
}

interface PutAccountAppParams {
  accountId: string;
  appId: string;
  data: Partial<AppInfo>;
}

interface PatchAccountAppParams {
  accountId: string;
  appId: string;
  newData: Partial<Application>;
  data?: Partial<Application>;
}

interface StripeCustomerPortalSessionResponse {
  url: string;
}

// Utils
const BASE_URL = process.env.REACT_APP_PORT_API_URL || '';
const jsonContentTypeHeader = { 'Content-Type': 'application/json' };

const getAuthHeader = async () => {
  const session = await Auth.currentSession();
  if (!session) throw new Error('user must be signed in to get JWT token');
  return session.getIdToken().getJwtToken();
};

async function portalFetch<ResponseFormat>(route: string, reqOptions?: RequestInit) {
  const Authorization = await getAuthHeader();
  const reqInit = { ...reqOptions, headers: { ...(reqOptions?.headers || {}), Authorization } };
  const response = await fetch(BASE_URL + route, reqInit);
  const json = await response.json();

  if (response.ok) {
    return json as ResponseFormat;
  }

  throw new Error(json.message);
}

// Account
export function getAccount({ accountId }: GetAccountParams): Promise<AccountResponse> {
  return portalFetch<AccountResponse>(`accounts/${accountId}`);
}

export async function putAccount({ accountId, data }: PutAccountParams): Promise<AccountResponse> {
  return portalFetch<AccountResponse>(`accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Account User
export async function getAccountUser({
  accountId,
  userId,
}: GetAccountUserParams): Promise<AccountUserResponse> {
  return portalFetch(`accounts/${accountId}/users/${userId}`);
}

export async function putAccountUser({
  accountId,
  userId,
  data,
}: PutAccountUserParams): Promise<AccountUserResponse> {
  return portalFetch<AccountUserResponse>(`accounts/${accountId}/users/${userId}`, {
    method: 'PUT',
    headers: jsonContentTypeHeader,
    body: JSON.stringify(data),
  });
}

// accountId:string, formData: AppInfo
export async function postAccountApp({
  accountId,
  data,
}: PostAccountAppParams): Promise<Application> {
  return portalFetch<Application>(`accounts/${accountId}/apps`, {
    method: 'POST',
    headers: jsonContentTypeHeader,
    body: JSON.stringify(data),
  });
}

export async function putAccountApp({
  accountId,
  appId,
  data,
}: PutAccountAppParams): Promise<Application> {
  return portalFetch<Application>(`accounts/${accountId}/apps/${appId}`, {
    method: 'PUT',
    headers: jsonContentTypeHeader,
    body: JSON.stringify(data),
  });
}

export async function patchAccountApp({
  accountId,
  appId,
  data,
  newData,
}: PatchAccountAppParams): Promise<Application> {
  return portalFetch<Application>(`accounts/${accountId}/apps/${appId}`, {
    method: 'PATCH',
    headers: jsonContentTypeHeader,
    body: JSON.stringify({ data, newData }),
  });
}

export async function getAccountApps({ accountId }: GetAccountAppsParams): Promise<AccountApps> {
  return portalFetch<AccountApps>(`accounts/${accountId}/apps`);
}

export async function getAccountAppApiKey({
  accountId,
  appId,
  apiKey,
}: GetAccountAppApiKeyParams): Promise<ApiKeyCounts> {
  return portalFetch<ApiKeyCounts>(`accounts/${accountId}/apps/${appId}/api-keys/${apiKey}`);
}

export async function getAppReport({
  accountId,
  appId,
  startDate,
  endDate,
  summary,
}: GetAppReportParams): Promise<AppReportResponse> {
  return portalFetch<AppReportResponse>(
    `accounts/${accountId}/apps/${appId}/report?startDate=${startDate}&endDate=${endDate}${
      summary ? `&summary=${summary}` : ''
    }`,
  );
}

// Checkout
export async function postCheckout(
  accountId: string,
  appId: string,
): Promise<{ sessionId: string; publishableKey: string }> {
  return portalFetch(`accounts/${accountId}/apps/${appId}/checkout`, {
    method: 'POST',
    headers: jsonContentTypeHeader,
    body: JSON.stringify({ accountId, appId }),
  });
}

export async function getStripeCustomerPortalUrl(
  accountId: string,
  appId: string,
): Promise<StripeCustomerPortalSessionResponse> {
  return portalFetch<StripeCustomerPortalSessionResponse>(
    `accounts/${accountId}/apps/${appId}/billing-portal-url`,
  );
}

export async function getBibles(): Promise<Bible[]> {
  const response = await fetch('/bibles.json');
  const json = await response.json();

  if (response.ok) {
    return json as Bible[];
  }

  throw new Error(json.message);
}

export async function getTermsAndConditions(): Promise<TermsAndConditions> {
  return portalFetch<TermsAndConditions>('terms-and-conditions');
}

export async function getPlans(): Promise<Plan[]> {
  return portalFetch<Plan[]>('plans');
}

export async function putSuperAdminPlan(plan: Plan): Promise<Plan> {
  return portalFetch<Plan>(`super-admin/plans/${plan.id}`, {
    method: 'PUT',
    headers: jsonContentTypeHeader,
    body: JSON.stringify(plan),
  });
}

export async function getSuperAdminApps(): Promise<AppListRecord[]> {
  return portalFetch<AppListRecord[]>('super-admin/apps');
}

export async function getSuperAdminAccountApp(
  accountId: string,
  appId: string,
): Promise<AccountAppDetails> {
  return portalFetch<AccountAppDetails>(`super-admin/accounts/${accountId}/apps/${appId}`);
}

export async function patchSuperAdminAccountApp({
  accountId,
  appId,
  data,
  newData,
}: PatchAccountAppParams): Promise<Application> {
  return portalFetch<Application>(`super-admin/accounts/${accountId}/apps/${appId}`, {
    method: 'PATCH',
    headers: jsonContentTypeHeader,
    body: JSON.stringify({ data, newData }),
  });
}
